<script setup>
import { Head } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import VariantPageWrapper from '@/Components/VariantPageWrapper.vue';
import { isBrandDirect as checkBrandDirect } from '@/lib/brand';
import LeadFunnel from '@/Components/LeadFunnel.vue';
import { useDesignSet } from '@/composables/useDesignSet';

const props = defineProps({
    domain: Object,
    service: String,
    branding: Object,
    designVariant: Object,
    spotImages: Object,
});

const { isClassic } = useDesignSet();

const accent = props.domain?.settings?.brand_accent
    || props.domain?.settings?.brand_color
    || '#ea580c';

const isBrandDirect = checkBrandDirect(props.domain);
const brandCta = props.domain?.settings?.brand_cta || 'Jetzt beraten lassen';

const serviceLabel = props.service === 'wp' ? 'Wärmepumpen' : 'Photovoltaik';
const serviceSingular = props.service === 'wp' ? 'Wärmepumpe' : 'Solaranlage';
const stepSpots = ['how_step_1', 'how_step_2', 'how_step_3', 'how_step_4'];

const wpSteps = [
    { title: 'Kostenlose Beratung', desc: 'In einem ersten Gespräch klären wir Ihre Anforderungen und prüfen die Machbarkeit für Ihr Objekt. Unser Online-Fragebogen dauert nur 2 Minuten.' },
    { title: 'Individuelle Planung', desc: 'Unsere Experten erstellen ein maßgeschneidertes Konzept auf Basis von Heizlast, Gebäudetyp und vorhandener Heiztechnik.' },
    { title: 'Angebotsvergleich', desc: 'Wir leiten Ihre Anfrage an regionale Fachbetriebe weiter. Sie können bis zu drei unverbindliche Angebote vergleichen.' },
    { title: 'Fachgerechte Montage', desc: 'Ihr ausgewähltes System wird von erfahrenen Fachbetrieben termingerecht installiert, inklusive Inbetriebnahme und Einweisung.' },
];

const pvSteps = [
    { title: 'Gebäude beschreiben', desc: 'Gebäudetyp, beheizte Fläche, Strom- und Wärmeverbrauch sowie PLZ. Fehlende Werte schätzen wir konservativ.' },
    { title: 'Angebote erhalten', desc: 'Wir leiten Ihre Anfrage an regionale Fachbetriebe weiter. Sie können bis zu drei unverbindliche Angebote erhalten. Anzahl und Rückmeldung sind nicht garantiert.' },
    { title: 'Vergleichen & entscheiden', desc: 'Vergleichen Sie die Angebote in Ruhe - Leistung, Preis, Garantien und Service. Wir helfen Ihnen, das beste Preis-Leistungs-Verhältnis zu finden.' },
    { title: 'Fachgerechte Installation', desc: 'Ihr ausgewählter Fachbetrieb installiert Ihre Solaranlage termingerecht, inklusive Anmeldung beim Netzbetreiber und Inbetriebnahme.' },
];

const steps = props.service === 'wp' ? wpSteps : pvSteps;

const qualityItems = [
    'Regionale Fachbetriebe',
    'Hochwertige Premium-Komponenten',
    'Persönlicher Ansprechpartner',
];
</script>

<template>
    <Head :title="`So funktioniert es - Der Weg zu Ihrer ${serviceSingular}`">
        <meta name="description" :content="`Erfahren Sie, wie Sie in 4 einfachen Schritten zu Ihrer neuen ${serviceSingular} kommen: Beratung, Planung, Angebotsvergleich und fachgerechte Montage.`" />
        <meta property="og:title" :content="`So funktioniert es - Der Weg zu Ihrer ${serviceSingular}`" />
        <meta property="og:type" content="website" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap" />
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="how" heroMode :hideChrome="!!domain?.settings?.design_variant">
        <VariantPageWrapper :domain="domain">
            <div class="pvhx-how" :style="{ '--pvhx-accent': accent }">
                <!-- Hero -->
                <section class="pvhx-hero">
                    <div class="pvhx-wrap">
                        <p class="pvhx-eyebrow">So funktioniert es</p>
                        <h1 class="pvhx-h1">So entsteht Ihre Hybrid-Auslegung.</h1>
                        <p class="pvhx-lead">Gebäudetyp, Fläche und Verbrauch eingeben - dann unverbindliche Angebote regionaler Fachbetriebe anfragen.</p>
                    </div>
                </section>

                <!-- Steps -->
                <section class="pvhx-section">
                    <div class="pvhx-wrap">
                        <div class="pvhx-steps-head">
                            <h2 class="pvhx-h2">In 4 Schritten zum Ziel</h2>
                            <p class="pvhx-sub">Von der Schätzung bis zur Anfrage - ohne Festpreis-Garantie.</p>
                        </div>

                        <div class="pvhx-steps">
                            <div
                                v-for="(step, idx) in steps"
                                :key="step.title"
                                class="pvhx-step"
                                :class="idx % 2 === 1 ? 'pvhx-step--reverse' : ''"
                            >
                                <div class="pvhx-step__media">
                                    <div
                                        class="pvhx-step__img"
                                        role="img"
                                        :aria-label="`Schritt ${idx + 1}: ${step.title}`"
                                        :style="spotImages?.[stepSpots[idx]] ? { backgroundImage: `url(${spotImages[stepSpots[idx]]})` } : {}"
                                    >
                                        <span class="pvhx-step__num">{{ idx + 1 }}</span>
                                    </div>
                                </div>
                                <div class="pvhx-step__content">
                                    <p class="pvhx-step__label">Schritt {{ idx + 1 }} von {{ steps.length }}</p>
                                    <h3 class="pvhx-h3">{{ step.title }}</h3>
                                    <p class="pvhx-p">{{ step.desc }}</p>
                                    <div class="pvhx-step__dots">
                                        <span
                                            v-for="dot in steps.length"
                                            :key="dot"
                                            class="pvhx-step__dot"
                                            :class="{ 'is-active': dot <= idx + 1 }"
                                        ></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Quality -->
                <section class="pvhx-section pvhx-section--alt">
                    <div class="pvhx-wrap pvhx-wrap--narrow pvhx-quality">
                        <h2 class="pvhx-h2 pvhx-h2--center">Was der Service leistet</h2>
                        <p class="pvhx-sub pvhx-sub--center">Regionale Fachbetriebe, KfW-Hinweise bis zu 70 Prozent, kostenlos und unverbindlich.</p>
                        <ul class="pvhx-quality__list">
                            <li v-for="item in qualityItems" :key="item" class="pvhx-quality__item">
                                <span class="pvhx-quality__check" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" width="16" height="16"><path d="M20 6 9 17l-5-5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" /></svg>
                                </span>
                                <span class="pvhx-quality__text">{{ item }}</span>
                            </li>
                        </ul>
                    </div>
                </section>

                <!-- CTA -->
                <section class="pvhx-cta">
                    <div class="pvhx-wrap pvhx-cta__inner">
                        <h2 class="pvhx-cta__title">Bereit für den nächsten Schritt?</h2>
                        <p class="pvhx-cta__sub">{{ isBrandDirect ? 'Fordern Sie jetzt Ihr kostenloses Angebot an und sparen Sie bares Geld.' : 'Starten Sie jetzt Ihren kostenlosen Vergleich und sparen Sie bares Geld.' }}</p>
                        <div class="pvhx-cta__funnel">
                            <LeadFunnel :domain="domain" :service="service" :design-variant="designVariant" bare :dark-mode="false" />
                        </div>
                    </div>
                </section>
            </div>
        </VariantPageWrapper>
    </AppLayout>
</template>

<style scoped>
/* pvhx-how-* / shared pvhx-* namespace, matched to PvKostenrechnerChrome /
   Home.vue (paper #f4f1ea, ink #1c1917, amber accent, Lora + Inter). */
.pvhx-how {
    --pvhx-ink: #1c1917;
    --pvhx-ink-2: #44403c;
    --pvhx-mute: #78716c;
    --pvhx-page: #f4f1ea;
    --pvhx-surface: #ffffff;
    --pvhx-line: #e4d8c6;
    --pvhx-line-2: #cfc0a8;
    background: var(--pvhx-page);
    color: var(--pvhx-ink);
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    font-size: 16px;
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
}

.pvhx-how h1,
.pvhx-how h2,
.pvhx-how h3 {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    letter-spacing: -0.015em;
}

.pvhx-wrap {
    max-width: 1240px;
    margin: 0 auto;
    padding: 0 32px;
}
.pvhx-wrap--narrow { max-width: 860px; }

@media (max-width: 600px) {
    .pvhx-wrap { padding: 0 20px; }
}

.pvhx-eyebrow {
    font-size: 11px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--pvhx-accent, #ea580c);
    font-weight: 600;
    margin: 0 0 18px;
}

.pvhx-h1 {
    font-size: clamp(38px, 5.4vw, 60px);
    line-height: 1.02;
    letter-spacing: -0.025em;
    color: var(--pvhx-ink);
    margin: 0 0 20px;
    max-width: 20ch;
}

.pvhx-h2 {
    font-size: clamp(26px, 3.4vw, 38px);
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: var(--pvhx-ink);
    margin: 0 0 12px;
}
.pvhx-h2--center { text-align: center; }

.pvhx-h3 {
    font-size: 22px;
    line-height: 1.25;
    color: var(--pvhx-ink);
    margin: 8px 0 12px;
}

.pvhx-p {
    font-size: 15px;
    line-height: 1.7;
    color: var(--pvhx-ink-2);
    margin: 0;
}

.pvhx-lead {
    font-size: 18px;
    line-height: 1.6;
    color: var(--pvhx-ink-2);
    max-width: 58ch;
    margin: 0;
}

.pvhx-sub {
    font-size: 16px;
    line-height: 1.6;
    color: var(--pvhx-ink-2);
}
.pvhx-sub--center { text-align: center; margin: 0 auto 28px; max-width: 52ch; }

/* Hero */
.pvhx-hero {
    padding: 88px 0 56px;
    border-bottom: 1px solid var(--pvhx-line);
    position: relative;
}
.pvhx-hero::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(circle at 90% -20%, rgba(234, 88, 12, 0.12), transparent 45%);
}
.pvhx-hero .pvhx-eyebrow,
.pvhx-hero .pvhx-h1,
.pvhx-hero .pvhx-lead { position: relative; }

/* Sections */
.pvhx-section {
    padding: 72px 0;
    border-bottom: 1px solid var(--pvhx-line);
}
.pvhx-section--alt { background: #ebe4d6; }

.pvhx-steps-head {
    text-align: center;
    margin-bottom: 48px;
}

/* Steps */
.pvhx-steps {
    display: flex;
    flex-direction: column;
    gap: 48px;
}

@media (min-width: 1024px) {
    .pvhx-steps { gap: 64px; }
}

.pvhx-step {
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: center;
}

@media (min-width: 1024px) {
    .pvhx-step { flex-direction: row; gap: 56px; }
    .pvhx-step--reverse { flex-direction: row-reverse; }
    .pvhx-step__media,
    .pvhx-step__content { width: 50%; }
}

.pvhx-step__media { width: 100%; flex-shrink: 0; }

.pvhx-step__img {
    position: relative;
    aspect-ratio: 16 / 10;
    border-radius: 6px;
    overflow: hidden;
    background: var(--pvhx-surface);
    background-size: cover;
    background-position: center;
    border: 1px solid var(--pvhx-line);
}

@media (min-width: 1024px) {
    .pvhx-step__img { aspect-ratio: 4 / 3; }
}

.pvhx-step__num {
    position: absolute;
    top: 18px;
    left: 18px;
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 18px;
    color: #fff;
    background: var(--pvhx-accent, #ea580c);
    border-radius: 50%;
    box-shadow: 0 0 0 4px rgba(251, 246, 236, 0.7);
}

.pvhx-step__content { width: 100%; }

.pvhx-step__label {
    font-size: 12px;
    font-weight: 600;
    color: var(--pvhx-accent, #ea580c);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    margin: 0;
}

.pvhx-step__dots {
    display: flex;
    gap: 8px;
    margin-top: 20px;
}

.pvhx-step__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--pvhx-line-2);
    transition: background 0.2s ease, transform 0.2s ease;
}

.pvhx-step__dot.is-active {
    background: var(--pvhx-accent, #ea580c);
    transform: scale(1.15);
}

/* Quality */
.pvhx-quality__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
    max-width: 420px;
    margin: 0 auto;
}

@media (min-width: 768px) {
    .pvhx-quality__list { grid-template-columns: repeat(3, 1fr); max-width: none; }
}

.pvhx-quality__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px;
    background: var(--pvhx-surface);
    border: 1px solid var(--pvhx-line);
    border-radius: 6px;
}

.pvhx-quality__check {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: rgba(234, 88, 12, 0.12);
    color: var(--pvhx-accent, #ea580c);
}

.pvhx-quality__text {
    font-size: 14px;
    font-weight: 500;
    color: var(--pvhx-ink);
    line-height: 1.4;
}

/* CTA */
.pvhx-cta {
    background: #1c1917;
    color: #f4f1ea;
}

.pvhx-cta__inner {
    padding: 72px 32px;
    text-align: center;
}

.pvhx-cta__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: clamp(28px, 3.6vw, 40px);
    line-height: 1.1;
    letter-spacing: -0.025em;
    color: #f4f1ea;
    margin: 0 0 14px;
}

.pvhx-cta__sub {
    font-size: 16px;
    line-height: 1.6;
    color: rgba(251, 246, 236, 0.7);
    max-width: 56ch;
    margin: 0 auto 32px;
}

.pvhx-cta__funnel {
    max-width: 640px;
    margin: 0 auto;
    background: var(--pvhx-surface);
    border: 1px solid var(--pvhx-line);
    border-radius: 6px;
    padding: 32px;
    text-align: left;
}

@media (max-width: 600px) {
    .pvhx-hero { padding: 56px 0 40px; }
    .pvhx-section { padding: 48px 0; }
    .pvhx-steps-head { margin-bottom: 36px; }
    .pvhx-cta__inner { padding: 56px 20px; }
    .pvhx-cta__funnel { padding: 22px; }
}

@media (prefers-reduced-motion: reduce) {
    .pvhx-how *, .pvhx-how *::before, .pvhx-how *::after {
        transition: none !important;
        animation: none !important;
    }
}
</style>
